/**
 * Contact details (admin mode): the member's phone numbers and email addresses (membermanagement 003).
 *
 * Each has a label (Mobile, Home, Work, Other) and what it is for (Calling, WhatsApp, SMS, Email), with one main
 * per use. The main Calling number is the member's cellphone number and the main email is the member's email
 * everywhere else in CCMS (the API keeps them in step), so the profile header is refreshed after every change.
 * API: api/members/{id}/contact-points (read: Member.Read; change: Member.Write).
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconPlus, IconEdit, IconTrash, IconAlertCircle, IconPhone, IconMail, IconStarFilled, IconDotsVertical, IconAddressBook,
} from '@tabler/icons-react';
import {
  Card, Stack, Group, Button, TextInput, SimpleGrid, Text, Alert, Modal, Skeleton, Tooltip, ActionIcon, Badge, ThemeIcon,
  EmptyState, Select, Checkbox, SegmentedControl, Menu,
} from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';
import { modal } from '@/components/ui/providers/ModalsProvider';
import {
  useMemberService, MemberContactIds,
  type MemberContactPointView, type MemberContactLookups, type MemberContactLookupItem,
} from '@actschurch/ccms-sdk';
import type { MemberTabProps } from '../types';
import { useInvalidateMember } from '../useMemberMutations';
import { errorMessage } from '../memberFormat';
import {
  looksLikePhone, looksLikeEmail, usesForKind, labelsForKind, defaultUsesForKind, defaultLabelForKind, USE_COLORS,
} from '../memberDetailsFormat';

/** Lookup query key shared by every profile (the lookups are seeded and rarely change). */
export const CONTACT_LOOKUPS_KEY = ['members', 'contact-lookups'] as const;

const FALLBACK_LOOKUPS: MemberContactLookups = {
  kinds: [
    { id: 1, name: 'Phone', sortOrder: 1, isActive: true },
    { id: 2, name: 'Email', sortOrder: 2, isActive: true },
  ],
  labels: [
    { id: 1, name: 'Mobile', kindID: 1, sortOrder: 1, isActive: true },
    { id: 2, name: 'Home', sortOrder: 2, isActive: true },
    { id: 3, name: 'Work', sortOrder: 3, isActive: true },
    { id: 4, name: 'Other', sortOrder: 4, isActive: true },
  ],
  uses: [
    { id: 1, name: 'Calling', kindID: 1, sortOrder: 1, isActive: true },
    { id: 2, name: 'WhatsApp', kindID: 1, sortOrder: 2, isActive: true },
    { id: 3, name: 'SMS', kindID: 1, sortOrder: 3, isActive: true },
    { id: 4, name: 'Email', kindID: 2, sortOrder: 4, isActive: true },
  ],
};

interface PointForm {
  kindID: number;
  labelID: string | null;
  value: string;
  useIDs: number[];
  primaryUseIDs: number[];
  note: string;
}

function emptyForm(kindID: number): PointForm {
  return { kindID, labelID: String(defaultLabelForKind(kindID)), value: '', useIDs: defaultUsesForKind(kindID), primaryUseIDs: [], note: '' };
}

export function ContactListCard({ api, member, perms }: MemberTabProps) {
  const memberService = useMemberService();
  const queryClient = useQueryClient();
  const invalidateMember = useInvalidateMember(api);
  const memberId = member.memberID;

  const lookupsQuery = useQuery({ queryKey: CONTACT_LOOKUPS_KEY, queryFn: () => memberService.getContactLookups(), staleTime: 60 * 60 * 1000 });
  const lookups = lookupsQuery.data && lookupsQuery.data.uses.length > 0 ? lookupsQuery.data : FALLBACK_LOOKUPS;
  const points = useQuery({ queryKey: api.keys.contactPoints, queryFn: () => memberService.getContactPoints(memberId), enabled: memberId > 0 });

  const [editing, setEditing] = useState<{ point: MemberContactPointView | null } | null>(null);
  const [form, setForm] = useState<PointForm>(emptyForm(MemberContactIds.KindPhone));

  const afterWrite = (list: MemberContactPointView[], title: string) => {
    queryClient.setQueryData(api.keys.contactPoints, list);
    // The main Calling number and main email are the member's cellphone and email: refresh the header and lists.
    invalidateMember();
    queryClient.invalidateQueries({ queryKey: api.keys.audit });
    toast.success(title);
  };

  const save = useMutation({
    mutationFn: () => {
      const labelID = form.labelID ? Number(form.labelID) : null;
      if (editing?.point) {
        const p = editing.point;
        return memberService.updateContactPoint(memberId, p.contactPointID, {
          labelID: labelID !== p.labelID ? labelID : undefined,
          value: form.value.trim() !== p.value ? form.value.trim() : undefined,
          useIDs: form.useIDs,
          note: form.note.trim() !== (p.note ?? '') ? form.note.trim() : undefined,
        });
      }
      return memberService.addContactPoint(memberId, {
        kindID: form.kindID,
        labelID,
        value: form.value.trim(),
        useIDs: form.useIDs,
        primaryUseIDs: form.primaryUseIDs.filter((u) => form.useIDs.includes(u)),
        note: form.note.trim() || null,
      });
    },
    onSuccess: (list) => { afterWrite(list, editing?.point ? 'Contact detail updated' : 'Contact detail added'); setEditing(null); },
    onError: (e) => toast.error('Could not save the contact detail', errorMessage(e)),
  });

  const makeMain = useMutation({
    mutationFn: ({ point, useId }: { point: MemberContactPointView; useId: number }) => memberService.setPrimaryContactPoint(memberId, point.contactPointID, useId),
    onSuccess: (list, { useId }) => {
      const useName = lookups.uses.find((u) => u.id === useId)?.name;
      afterWrite(list, useName ? `Main ${useName} contact changed` : 'Main contact changed');
    },
    onError: (e) => toast.error('Could not change the main contact detail', errorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (point: MemberContactPointView) => memberService.deleteContactPoint(memberId, point.contactPointID),
    onSuccess: (list) => afterWrite(list, 'Contact detail removed'),
    onError: (e) => toast.error('Could not remove the contact detail', errorMessage(e)),
  });

  const list = points.data ?? [];
  const mains = useMemo(() => {
    const out: { use: MemberContactLookupItem; point: MemberContactPointView | null }[] = [];
    for (const use of [...lookups.uses].filter((u) => u.isActive).sort((a, b) => a.sortOrder - b.sortOrder)) {
      out.push({ use, point: list.find((p) => p.uses.some((u) => u.useID === use.id && u.isPrimary)) ?? null });
    }
    return out;
  }, [lookups.uses, list]);

  const openAdd = (kindID: number = MemberContactIds.KindPhone) => { setForm(emptyForm(kindID)); save.reset(); setEditing({ point: null }); };
  const openEdit = (p: MemberContactPointView) => {
    setForm({ kindID: p.kindID, labelID: String(p.labelID), value: p.value, useIDs: p.uses.map((u) => u.useID), primaryUseIDs: [], note: p.note ?? '' });
    save.reset();
    setEditing({ point: p });
  };

  const kindUses = usesForKind(lookups.uses, form.kindID);
  const kindLabels = labelsForKind(lookups.labels, form.kindID);
  const isEmail = form.kindID === MemberContactIds.KindEmail;
  const valueError = form.value.trim() && !(isEmail ? looksLikeEmail(form.value) : looksLikePhone(form.value))
    ? (isEmail ? 'Enter a valid email address' : 'Enter a valid phone number')
    : null;
  const canSave = !!form.value.trim() && !valueError && form.useIDs.length > 0;

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Text fw={600}>Contact details</Text>
            <Text size="sm" c="dimmed">Phone numbers and email addresses. Choose which one to use for calls, WhatsApp, SMS and email.</Text>
          </div>
          {perms.edit && (
            <Group gap="xs">
              <Button size="sm" variant="light" leftSection={<IconPhone size={16} />} onClick={() => openAdd(MemberContactIds.KindPhone)}>Add number</Button>
              <Button size="sm" variant="light" leftSection={<IconMail size={16} />} onClick={() => openAdd(MemberContactIds.KindEmail)}>Add email</Button>
            </Group>
          )}
        </Group>

        {points.isLoading && <Stack gap="sm"><Skeleton height={60} /><Skeleton height={60} /></Stack>}
        {points.isError && (
          <Alert color="red" icon={<IconAlertCircle size={16} />} title="Could not load the contact details">{errorMessage(points.error)}</Alert>
        )}

        {!points.isLoading && !points.isError && list.length > 0 && (
          <Group gap="xs" wrap="wrap">
            {mains.map(({ use, point }) => (
              <Badge key={use.id} variant={point ? 'light' : 'outline'} color={point ? USE_COLORS[use.id] ?? 'gray' : 'gray'} size="lg" radius="sm"
                style={{ textTransform: 'none', fontWeight: 500 }}>
                {use.name}: {point ? point.displayValue : 'not set'}
              </Badge>
            ))}
          </Group>
        )}

        {!points.isLoading && !points.isError && list.length === 0 && (
          <EmptyState size="sm" icon={<IconAddressBook size={32} />} title="No contact details"
            description={perms.edit ? 'Add a phone number or email address so the church can reach this person.' : 'No phone number or email address has been recorded.'}
            actionLabel={perms.edit ? 'Add number' : undefined} onAction={perms.edit ? () => openAdd(MemberContactIds.KindPhone) : undefined} />
        )}

        <SimpleGrid cols={{ base: 1, md: 2 }}>
          {list.map((p) => {
            const email = p.kindID === MemberContactIds.KindEmail;
            const fitting = usesForKind(lookups.uses, p.kindID);
            const notMainFor = fitting.filter((u) => !p.uses.some((x) => x.useID === u.id && x.isPrimary));
            return (
              <Card key={p.contactPointID} withBorder padding="md" radius="md">
                <Group justify="space-between" align="flex-start" wrap="nowrap">
                  <Group gap="sm" align="flex-start" wrap="nowrap" style={{ minWidth: 0 }}>
                    <ThemeIcon variant="light" color={email ? 'grape' : 'blue'} size="lg">{email ? <IconMail size={18} /> : <IconPhone size={18} />}</ThemeIcon>
                    <Stack gap={4} style={{ minWidth: 0 }}>
                      <Group gap={6} wrap="wrap">
                        <Text fw={600} size="sm" style={{ wordBreak: 'break-all' }}>{p.displayValue}</Text>
                        <Badge size="xs" variant="outline" color="gray">{p.labelName ?? lookups.labels.find((l) => l.id === p.labelID)?.name}</Badge>
                      </Group>
                      <Group gap={4} wrap="wrap">
                        {p.uses.map((u) => (
                          <Badge key={u.useID} size="sm" radius="sm" variant={u.isPrimary ? 'filled' : 'light'} color={USE_COLORS[u.useID] ?? 'gray'}
                            leftSection={u.isPrimary ? <IconStarFilled size={10} /> : undefined} style={{ textTransform: 'none' }}>
                            {u.name}{u.isPrimary ? ' (main)' : ''}
                          </Badge>
                        ))}
                      </Group>
                      {p.note && <Text size="xs" c="dimmed">{p.note}</Text>}
                      {!p.normalizedValue && !email && (
                        <Text size="xs" c="orange">Not a number we can dial. Edit it to fix it.</Text>
                      )}
                    </Stack>
                  </Group>
                  {perms.edit && (
                    <Group gap={2} wrap="nowrap">
                      <Tooltip label="Edit"><ActionIcon variant="subtle" aria-label="Edit contact detail" onClick={() => openEdit(p)}><IconEdit size={16} /></ActionIcon></Tooltip>
                      <Menu position="bottom-end" withinPortal>
                        <Menu.Target>
                          <ActionIcon variant="subtle" aria-label="More actions" loading={makeMain.isPending && makeMain.variables?.point.contactPointID === p.contactPointID}>
                            <IconDotsVertical size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          {notMainFor.length > 0 && <Menu.Label>Make it the main one for</Menu.Label>}
                          {notMainFor.map((u) => (
                            <Menu.Item key={u.id} leftSection={<IconStarFilled size={14} />} onClick={() => makeMain.mutate({ point: p, useId: u.id })}>
                              {u.name}
                            </Menu.Item>
                          ))}
                          {notMainFor.length > 0 && <Menu.Divider />}
                          <Menu.Item color="red" leftSection={<IconTrash size={14} />}
                            onClick={() => modal.confirm({
                              title: `Remove ${p.displayValue}?`,
                              body: p.uses.some((u) => u.isPrimary)
                                ? 'It is a main contact detail. The next one with the same use becomes the main one; if there is none, the member has no main for that use.'
                                : 'It will be removed from the contact details.',
                              confirmLabel: 'Remove',
                              confirmColor: 'red',
                              onConfirm: () => remove.mutate(p),
                            })}>
                            Remove
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>
                  )}
                </Group>
              </Card>
            );
          })}
        </SimpleGrid>
      </Stack>

      <Modal opened={!!editing} onClose={() => setEditing(null)} centered size="md"
        title={editing?.point ? 'Edit contact detail' : isEmail ? 'Add an email address' : 'Add a phone number'}>
        <Stack gap="md">
          {!editing?.point && (
            <SegmentedControl fullWidth value={String(form.kindID)}
              data={[{ value: String(MemberContactIds.KindPhone), label: 'Phone' }, { value: String(MemberContactIds.KindEmail), label: 'Email' }]}
              onChange={(v) => setForm({ ...emptyForm(Number(v)), value: form.value })} />
          )}
          <TextInput label={isEmail ? 'Email address' : 'Phone number'} required data-autofocus
            placeholder={isEmail ? 'name@example.org' : 'For example 082 123 4567 or +44 20 7946 0958'}
            value={form.value} error={valueError} onChange={(e) => setForm({ ...form, value: e.currentTarget.value })} />
          <Select label="Label" data={kindLabels.map((l) => ({ value: String(l.id), label: l.name }))} value={form.labelID}
            onChange={(v) => setForm({ ...form, labelID: v })} allowDeselect={false} />
          <Stack gap={6}>
            <Text size="sm" fw={500}>What it is for</Text>
            <Group gap="md">
              {kindUses.map((u) => (
                <Checkbox key={u.id} label={u.name} checked={form.useIDs.includes(u.id)}
                  onChange={(e) => {
                    const useIDs = e.currentTarget.checked ? [...form.useIDs, u.id] : form.useIDs.filter((x) => x !== u.id);
                    setForm({ ...form, useIDs, primaryUseIDs: form.primaryUseIDs.filter((x) => useIDs.includes(x)) });
                  }} />
              ))}
            </Group>
            {form.useIDs.length === 0 && <Text size="xs" c="red">Tick at least one use.</Text>}
          </Stack>
          {!editing?.point && kindUses.length > 1 && form.useIDs.length > 0 && (
            <Stack gap={6}>
              <Text size="sm" fw={500}>Make it the main one for</Text>
              <Text size="xs" c="dimmed">A use with no main gets this one anyway.</Text>
              <Group gap="md">
                {kindUses.filter((u) => form.useIDs.includes(u.id)).map((u) => (
                  <Checkbox key={u.id} label={u.name} checked={form.primaryUseIDs.includes(u.id)}
                    onChange={(e) => setForm({
                      ...form,
                      primaryUseIDs: e.currentTarget.checked ? [...form.primaryUseIDs, u.id] : form.primaryUseIDs.filter((x) => x !== u.id),
                    })} />
                ))}
              </Group>
            </Stack>
          )}
          {editing?.point && (
            <Text size="xs" c="dimmed">To make it the main one for a use, use the menu next to it on the list.</Text>
          )}
          <TextInput label="Note" placeholder="For example Only after 5pm" maxLength={200} value={form.note}
            onChange={(e) => setForm({ ...form, note: e.currentTarget.value })} />
          {save.isError && <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMessage(save.error)}</Alert>}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditing(null)}>Cancel</Button>
            <Button leftSection={editing?.point ? undefined : <IconPlus size={16} />} loading={save.isPending} disabled={!canSave} onClick={() => save.mutate()}>
              {editing?.point ? 'Save' : 'Add'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
